import { Injectable }                   from '@angular/core';
import { ARCHIVING_IMPORT_METADATA }    from '@app/modules/archiving/config/archiving-import.config';
import { ArchivingErs }                 from '@app/modules/archiving/models/archiving-ers.model';
import {
	ArchivingExport,
	ArchivingExportTypeEnum
}                                       from '@app/modules/archiving/models/archiving-export.model';
import {
	ArchivingImport,
	ArchivingImportStatusEnum
}                                       from '@app/modules/archiving/models/archiving-import.model';
import {
	ArchivingImportsDetailPageRoute,
	ArchivingRoutesEnum
}                                       from '@app/modules/archiving/routes/archiving.routes';
import { ArchivingExportService }       from '@app/modules/archiving/services/archiving-export.service';
import {
	IPropsArchivingImportExport,
	IPropsUpdateArchivingImportState
}                                       from '@app/modules/archiving/store/archiving-import/archiving-import.actions';
import { archivingImportStore }         from '@app/modules/archiving/store/archiving-import/archiving-import.store';
import { BusinessPremisesRoutesEnum }   from '@app/modules/business-premises/routes/business-premises.routes';
import { AbstractBaseService }          from '@core/bases/services/abstractBaseService';
import {
	IArchivingErsHttpParams,
	IArchivingImportHttpParams,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                       from '@core/interfaces/api.interface';
import { AllowedDetailsTabEnum }        from '@core/interfaces/common.interfaces';
import { RouteBasePath }                from '@core/interfaces/route.interface';
import {
	IStoreConfig,
	StoreActionsEnum
}                                       from '@core/interfaces/store.interface';
import { ArchivingErsApiService }       from '@http/archiving/archiving-ers-api.service';
import { ArchivingImportApiService }    from '@http/archiving/archiving-import-api.service';
import { Store }                        from '@ngrx/store';
import { FilterIdentifiersEnum }        from '@shared/list-operations/model/filter.model';
import { ListOperationsIdentifierEnum } from '@shared/list-operations/model/list-operations.model';
import { actionStatus }                 from '@shared/services/toaster.service';
import { DateUtils }                    from '@utils/helpers';
import { Observable }                   from 'rxjs';
import { take }                         from 'rxjs/operators';


@Injectable( {
	providedIn: 'root'
} )
export class ArchivingImportService extends AbstractBaseService {

	constructor(
		private readonly archivingImportApiService: ArchivingImportApiService,
		private readonly archivingErsApiService: ArchivingErsApiService,
		private readonly archivingExportService: ArchivingExportService,
		protected readonly store$: Store
	) {
		super( store$, archivingImportStore.selectors, archivingImportStore.actions );
		this.logger.trace( 'ArchivingImportService constructor' );
	}

	private prepareApiParams( params: IArchivingImportHttpParams, businessPremisesId: string ): IArchivingImportHttpParams {
		const resultParams: IArchivingImportHttpParams = { ...this.generateParams( params ), businessPremisesId };
		const userLocale: string = this.uiService.getLanguage( true );

		if ( params && params.activeFilters?.[ FilterIdentifiersEnum.BUSINESS_DATE ] ) {
			if ( params.activeFilters[ FilterIdentifiersEnum.BUSINESS_DATE ]?.param[ 0 ] ) {
				resultParams.businessDateFrom = DateUtils.formatDateToLocale(
					params.activeFilters?.[ FilterIdentifiersEnum.BUSINESS_DATE ]?.param[ 0 ], DateUtils.ISO_DATE_FORMAT, userLocale
				);
			}
			if ( params.activeFilters[ FilterIdentifiersEnum.BUSINESS_DATE ]?.param[ 1 ] ) {
				resultParams.businessDateTo = DateUtils.formatDateToLocale(
					params.activeFilters?.[ FilterIdentifiersEnum.BUSINESS_DATE ]?.param[ 1 ], DateUtils.ISO_DATE_FORMAT, userLocale
				);
			}
		}

		if ( params && params?.activeFilters[ FilterIdentifiersEnum.CREATED_DATE ] ) {
			if ( params.activeFilters[ FilterIdentifiersEnum.CREATED_DATE ]?.param[ 0 ] ) {
				resultParams.createdDateFrom = DateUtils.formatDateToLocale(
					params?.activeFilters[ FilterIdentifiersEnum.CREATED_DATE ]?.param[ 0 ], DateUtils.ISO_DATE_FORMAT, userLocale
				);
			}
			if ( params.activeFilters[ FilterIdentifiersEnum.CREATED_DATE ]?.param[ 1 ] ) {
				resultParams.createdDateTo = DateUtils.formatDateToLocale(
					params?.activeFilters[ FilterIdentifiersEnum.CREATED_DATE ]?.param[ 1 ], DateUtils.ISO_DATE_FORMAT, userLocale
				);
			}
		}

		return resultParams;
	}

	// ARCHIVING IMPORT SPECIFIC SELECTORS ###########################################################################

	// ARCHIVING IMPORT SPECIFIC ACTIONS ###########################################################################
	updateStoreState( updateArchivingImportState: IPropsUpdateArchivingImportState ): void {
		this.store$.dispatch( archivingImportStore.actions.updateArchivingImportState( updateArchivingImportState ) );
	}

	// Entity Events ##############################################################################
	private showArchivingImportExport( businessPremisesId: string, showOnlyMessage: boolean = false ): void {
		const params: IPropsArchivingImportExport = {
			caller: 'ArchivingImportService:showArchivingImportExportModalWindow',
			showOnlyMessage,
			businessPremisesId
		};

		this.store$.dispatch( archivingImportStore.actions.showArchivingImportExportModal( params ) );
	}

	private createAndShowResultArchivingImportExport(
		archivingImportId: string,
		type: ArchivingExportTypeEnum,
		businessPremisesId: string,
		archivingImportEntityId: string
	): void {
		this.uiService.showPageLoaderAndCountUp();
		this.archivingExportService.createExports( { importIds: [ archivingImportId ], type }, businessPremisesId )
		.pipe( take( 1 ) )
		.subscribe( ( exportDataState: RequestCompleteEntityState<ArchivingExport> ) => {
			this.uiService.hidePageLoaderOrCountDown();

			if ( exportDataState.requestState === RequestState.SUCCESS ) {
				this.showArchivingImportExport( businessPremisesId, true );
			}
			this.toasterService.dispatch( exportDataState, archivingImportEntityId, actionStatus.EXPORT, true );
		} );
	}

	private deleteImport( archivingImport: ArchivingImport, navigateBack: boolean ): void {
		const dialogConfirmationText = this.translationService.getI18nString(
			true,
			ARCHIVING_IMPORT_METADATA.i18n.basepath,
			'entity.deleteDialog.description'
		);
		this.dialogCommonService.showConfirmationDialog( dialogConfirmationText )
		.pipe( take( 1 ) )
		.subscribe( () => {
			this.uiService.showPageLoaderAndCountUp();
			this.delete( archivingImport )
			.pipe( take( 1 ) )
			.subscribe( ( archivingImportState: RequestCompleteEntityState<ArchivingImport> ) => {
				if ( archivingImportState.requestState === RequestState.SUCCESS ) {
					this.storeRemoveOne<ArchivingImport>( archivingImport.id );

					if ( navigateBack ) {
						this.navigateBack();
					} else { // rehydration is needed because of status changes of connected replaced entities
						this.searchWithStoredListOperationsData(
							archivingImport.businessPremisesId,
							ListOperationsIdentifierEnum.BUSINESS_PREMISES_DETAIL_PAGE_ARCHIVE_IMPORTS,
							'ArchivingImportService:deleteImport'
						)
						.pipe( take( 1 ) )
						.subscribe();
					}
				}
				this.uiService.hidePageLoaderOrCountDown();
				this.toasterService.dispatch( archivingImportState, ARCHIVING_IMPORT_METADATA.entityId, actionStatus.DELETE );
			} );
		} );
	}

	archivingImportDeleteOnDetailPage( archivingImport: ArchivingImport ): void {
		this.deleteImport( archivingImport, true );
	}

	archivingImportDeleteOnEntity( archivingImport: ArchivingImport ): void {
		this.deleteImport( archivingImport, false );
	}

	showArchivingImportExportOnDetailPage(
		archivingImportId: string,
		type: ArchivingExportTypeEnum,
		businessPremisesId: string,
		archivingImportEntityId: string
	): void {
		this.createAndShowResultArchivingImportExport( archivingImportId, type, businessPremisesId, archivingImportEntityId );
	}

	showArchivingImportExportOnEntity(
		archivingImportId: string,
		type: ArchivingExportTypeEnum,
		businessPremisesId: string,
		archivingImportEntityId: string
	): void {
		this.createAndShowResultArchivingImportExport( archivingImportId, type, businessPremisesId, archivingImportEntityId );
	}

	showArchivingImportExportOnTab( businessPremisesId: string ): void {
		this.showArchivingImportExport( businessPremisesId, false );
	}


	// NAVIGATION #################################################################################
	navigateBack( caller = '', shouldRestoreListOperationData = true ): void {
		this.listOperationsService.state.listOperationsSetRestoreFlag(
			ListOperationsIdentifierEnum.BUSINESS_PREMISES_DETAIL_PAGE_ARCHIVE_IMPORTS,
			shouldRestoreListOperationData,
			caller
		);

		this.routerService.navigateBack(
			this.routerService.getFallbackRoute( RouteBasePath.BusinessPremises, BusinessPremisesRoutesEnum.ListPage ),
			caller + ' | ArchivingService:navigateBack'
		);
	}

	navigateToImportsDetailPage( importId: string ): void {
		if ( importId ) {
			const routeDefinition: ArchivingImportsDetailPageRoute = {
				basePath: RouteBasePath.ArchivingImports,
				path: ArchivingRoutesEnum.ImportsDetailPageTab,
				params: {
					importId,
					tab: AllowedDetailsTabEnum.CASH_POINT_CLOSING
				}
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'ArchivingImportService:navigateToImportsDetailPage'
			);
		}
	}

	// API #################################################################################
	searchWithStoredListOperationsData(
		businessPremisesId: string,
		listOperationsId: ListOperationsIdentifierEnum,
		caller: string
	): Observable<RequestCompleteListState<ArchivingImport>> {
		return this.listOperationsService.helper.requestWithStoredListOperationsData<ArchivingImport, IArchivingImportHttpParams>(
			( [ businessPremisesIdArg, listOperationsArg, callerArg, storeConfigArg ] ) =>
				this.search( businessPremisesIdArg, listOperationsArg, callerArg, storeConfigArg ),
			[ businessPremisesId, `${ caller } | ArchivingImportService:searchWithStoredListOperationsData`, null ],
			1,
			listOperationsId
		);
	}

	search(
		businessPremisesId: string,
		params?: IArchivingImportHttpParams,
		caller?: string,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteListState<ArchivingImport>> {
		// default storeConfig setup for search as long as no search selector exists
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		let storeProcessingConfig = this.createListStoreProcessingConfig<ArchivingImport>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeList$<ArchivingImport>(),
			StoreActionsEnum.SET_ALL
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ArchivingImportService:search` };

		params = this.prepareApiParams( params, businessPremisesId );

		const req$ = this.archivingImportApiService.search( params );
		return this.requestListThroughProxy<ArchivingImport>( req$, storeProcessingConfig );
	}

	read( id: string, withFullResponse?: boolean, caller?: string, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<ArchivingImport>> {
		// request from store must be skipped when withFullResponse is set true
		storeConfig = {
			getFromStoreFirst: withFullResponse ? false : storeConfig?.getFromStoreFirst,
			updateStore: storeConfig?.updateStore
		};

		let storeProcessingConfig = this.createEntityStoreProcessingConfig<ArchivingImport>(
			storeConfig,
			this.storeEntityById$<ArchivingImport>( id ),
			StoreActionsEnum.UPSERT_ONE
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ArchivingImportService:read` };

		const req$ = this.archivingImportApiService.read( id, withFullResponse );
		return this.requestEntityThroughProxy<ArchivingImport>( id, req$, storeProcessingConfig );
	}

	searchArchivingErsByBusinessPremisesId( businessPremisesId: string, params?: IArchivingErsHttpParams ): Observable<RequestCompleteListState<ArchivingErs>> {
		params = {
			...params,
			businessPremisesId
		};

		const req$ = this.archivingErsApiService.searchArchivingErsByBusinessPremisesId( params );
		return this.requestListThroughProxy<ArchivingErs>( req$ );
	}

	delete( archivingImport: ArchivingImport, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<ArchivingImport>> {
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<ArchivingImport>(
			storeConfig,
			null,
			StoreActionsEnum.REMOVE_ONE
		);

		const req$ = this.archivingImportApiService.delete( archivingImport.id );
		return this.requestEntityThroughProxy<ArchivingImport>( archivingImport.id, req$, storeProcessingConfig );
	}

	// HELPER METHODS
	public groupByReplaceId( list: ArchivingImport[] ): ArchivingImport[] {
		const map = new Map<string, ArchivingImport[]>();
		const replacedStatuses: ArchivingImportStatusEnum[] = [ ArchivingImportStatusEnum.REPLACED, ArchivingImportStatusEnum.BEING_REPLACED ];
		list.forEach( ( item: ArchivingImport ) => {
			const key = item.originImportId ? item.originImportId : replacedStatuses.find( status => item.status )
																															? item.id
																															: undefined;

			const collection = map.get( key );
			if ( !collection ) {
				map.set( key, [ item ] );
			} else {
				collection.push( item );
			}
		} );
		const result: ArchivingImport[] = [];
		map.forEach( importArray => result.push( ...importArray ) );
		return result;
	}
}
